// Карточка живого AIS-судна: всплывает у курсора при клике по точке слоя «Флот».
// Данные приходят свойствами GeoJSON-фичи (см. VesselProps), поэтому читаем их
// как «сырые» значения и приводим к нужному типу мягко — null/недостающее поле
// честно показываем прочерком, а не пустотой.

import { useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { cabinetApi } from '../api'
import { vesselKey } from './cardPick'
import { formatNumber, getLang, t, useT } from '../i18n'
import { pollCardRefresh, toCardProgress } from '../map/cardRefresh'
import { rfmoOrgsLabel, rfmoTargetLabel } from './rfmoTarget'
import { MapCallout } from '../map/MapCallout'
import { ownerRow } from './vesselIdentity'
import { fixCoords, fixTime } from './vesselFix'
import { vesselRefreshLabel } from './vesselRefresh'
import type { VesselRefreshProgress } from './vesselRefresh'

interface VesselCardProps {
  /** Позиция судна: к ней карточка и привязана (см. MapCallout). */
  anchor: { lng: number; lat: number }
  props: Record<string, unknown>
  /** Закреплена кликом: ловит мышь и показывает крестик. Незакреплённая
   *  (подсказка при наведении) прозрачна для мыши — иначе, наползая на курсор,
   *  она уводила бы его с точки и гасила сама себя. */
  pinned?: boolean
  onClose: () => void
  /** Позиция судна поехала — слою флота пора перезапроситься. Зовётся на каждом
   *  шаге опроса, а не только в конце: судно должно переехать, как только это
   *  стало известно. */
  onRefreshed?: () => void
  /** Трек ЭТОГО судна на карте: показать / скрыть. Без обработчика кнопки нет
   *  вовсе (карточка живёт и там, где слоя трека не бывает). */
  track?: {
    visible: boolean
    busy: boolean
    /** Почему трека не видно или сколько его — молчащая кнопка неотличима от
     *  сломанной. */
    hint: string | null
    onToggle: () => void
  }
  /** Прогнозный ход этого судна на сутки вперёд. Без обработчика кнопки нет
   *  вовсе — карточка живёт и там, где прогноза не бывает. */
  forecast?: {
    visible: boolean
    /** Почему линии нет или отчего ей веры меньше (см. forecastHint). */
    hint: string
    onToggle: () => void
  }
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

/** «последняя позиция …»: age_h — часы с последнего фикса.
 *  null → нет данных; <1ч → сейчас; <48ч → N ч назад; иначе N сут назад. */
export function lastSeenLabel(ageH: number | null): string {
  if (ageH == null) return t('models.vessel.noData')
  if (ageH < 1) return t('models.vessel.now')
  if (ageH < 48) return t('models.vessel.hoursAgo', { count: Math.round(ageH) })
  return t('models.vessel.daysAgo', { count: Math.round(ageH / 24) })
}

export function VesselCard({
  anchor, props, pinned = true, onClose, onRefreshed, track, forecast,
}: VesselCardProps) {
  const mmsi = str(props.mmsi)
  const tt = useT()
  const lang = getLang()
  const [progress, setProgress] = useState<VesselRefreshProgress | null>(null)
  // Карточка одна на все суда и не пересоздаётся при переходе курсора (ключа у
  // неё нет — см. cardPick.ts). Ход опроса принадлежит КОНКРЕТНОМУ судну, и на
  // чужой карточке он врал бы, поэтому снимаем его при смене судна.
  const shown = useRef(vesselKey({ props }))
  const now = vesselKey({ props })
  if (shown.current !== now) {
    shown.current = now
    if (progress) setProgress(null)
  }
  const busy = progress != null && !progress.done

  /** Спросить источники по этому судну: ShipFinder-API → кабинет → GFW.
   *
   *  Заведомо медленно (браузерный шаг — секунды), поэтому кнопка не ждёт
   *  результата молча: подпись под ней меняется по ходу опроса. Ошибку показываем
   *  тем же способом, что и успех, — молчащая кнопка неотличима от сломанной. */
  async function refresh() {
    if (!mmsi || busy) return
    setProgress({ processed: 0, total: 1, updated: 0, done: false, error: null })
    try {
      const { job_id } = await cabinetApi.refreshVessel(mmsi)
      if (!job_id) {
        setProgress({
          processed: 0, total: 1, updated: 0, done: true,
          error: 'models.vessel.refresh.notQueued',
        })
        return
      }
      await pollCardRefresh(job_id, {
        fetchStatus: cabinetApi.cardJobStatus,
        onProgress: status => {
          setProgress(toCardProgress(status))
          // На КАЖДОМ шаге, а не в конце: позиция уже в базе, и судну незачем
          // ждать закрытия задания, чтобы переехать на карте.
          if (status.updated > 0) onRefreshed?.()
        },
      })
    } catch {
      setProgress({
        processed: 0, total: 1, updated: 0, done: true,
        error: 'models.vessel.refresh.crashed',
      })
    }
  }

  const name = str(props.name) ?? str(props.mmsi) ?? tt('models.vessel.unnamed')
  const flag = str(props.flag_state) ?? '—'
  const company = str(props.company)
  const owner = ownerRow(props)
  const imo = str(props.imo) ?? '—'
  // Реестр судна показываем ТОЛЬКО когда он есть — и вместе с ним всё
  // остальное из реестра: реестры покрывают флот открытого моря, а в эфире 95
  // тысяч судов, и четыре строки вечных прочерков были бы шумом на каждой
  // карточке. Имя реестра при этом обязательно: «Сельдь атлантическая» и
  // «Сельдь» стоят в словаре соседними строками, и какой это океан, говорит
  // только он.
  const rfmoOrgs = rfmoOrgsLabel(props)
  const rfmoType = str(props.rfmo_type)
  const rfmoGear = str(props.rfmo_gear)
  const rfmoTarget = rfmoTargetLabel(props, lang)
  const ageH = num(props.age_h)
  // Где и когда снят фикс. До 21.08.2026 карточка отвечала только «сейчас», то
  // есть «младше часа», и сверить нас с другим источником было НЕЧЕМ: сличают
  // числа, а не слова (разбор LEDA777 — см. `vesselFix.ts`).
  const fixAt = fixTime(str(props.last_position_utc), lang)
  const coords = fixCoords(props, lang)
  const sog = num(props.sog)
  const cog = num(props.cog)
  const stale = props.stale === true

  return (
    <MapCallout anchor={anchor} width={240} pinned={pinned} draggable>
      <div className="flex items-start justify-between gap-2">
        {/* Ручка выноски: карточку уводят с места за шапку, а не за всё тело —
            в теле лежат MMSI, номер ИМО и владелец, и захват на всей площади
            отнял бы у них выделение мышью. */}
        <div
          data-callout-handle
          title={tt('models.vessel.dragHint')}
          className="min-w-0 cursor-grab active:cursor-grabbing"
        >
          <div className="truncate text-sm font-semibold text-foam" title={name}>
            {name}
          </div>
          <div className="tnum text-[11px] text-mist">MMSI {str(props.mmsi) ?? '—'}</div>
        </div>
        {pinned && (
          <button
            type="button"
            onClick={onClose}
            aria-label={tt('common.close')}
            className="-mr-1 -mt-1 rounded-md px-2 py-0.5 text-lg leading-none text-mist transition-colors hover:bg-haze hover:text-foam"
          >
            ×
          </button>
        )}
      </div>

      <dl className="mt-2 space-y-1 text-[12px]">
        <Row label={tt('models.vessel.flag')} value={flag} />
        <Row label={tt('models.vessel.imo')} value={imo} />
        {/* Владелец по реестру — справочное имя, и оговорка под ним объясняет,
            чем оно отличается от «Юр.лица»: реестровая запись имеет СРОК, и у
            половины судов он закрыт прошлым годом. */}
        <Row label={tt('models.vessel.owner')}>
          <span className="text-foam">{owner.value}</span>
          {owner.noteKey && (
            <span className="ml-1 text-[10px] text-mist">
              {tt(owner.noteKey, owner.noteParams)}
            </span>
          )}
        </Row>
        {/* «Юр.лицо» — ручная привязка судна к организации кабинета, ею живут
            фильтр «Свой флот» и анонимность мест вылова. У чужих судов её нет и
            быть не должно: строка с вечным прочерком только шумела бы. */}
        {company && <Row label={tt('models.vessel.company')} value={company} />}
        {/* Реестр описывает судно тремя полями, и тип корпуса из них самый
            пустой: у части флота комиссия его не указала вовсе («not specified
            (FX)»), а NEAFC пишет двухбуквенный код. Орудие и цель промысла при
            этом названы прямо — показывая один тип, карточка выглядела пустой
            при заполненном реестре. */}
        {rfmoOrgs && (
          <Row label={tt('models.vessel.rfmoOrgs')} value={rfmoOrgs} title={rfmoOrgs} />
        )}
        {rfmoType && (
          <Row label={tt('models.vessel.rfmoType')} value={rfmoType} title={rfmoType} />
        )}
        {rfmoGear && (
          <Row label={tt('models.vessel.rfmoGear')} value={rfmoGear} title={rfmoGear} />
        )}
        {rfmoTarget && (
          <Row label={tt('models.vessel.rfmoTarget')} value={rfmoTarget} title={rfmoTarget} />
        )}
        <Row label={tt('models.vessel.lastPosition')}>
          <span className={stale ? 'text-buoy' : 'text-foam'}>{lastSeenLabel(ageH)}</span>
        </Row>
        {/* Точное время и место фикса — подстрочником под «сейчас», во всю
            ширину карточки. В колонку значения они не влезают (карточка 240 px),
            а обрезанная координата хуже отсутствующей: «162,9…» правдоподобно и
            неверно. Приглушены намеренно — заголовок строки по-прежнему
            возраст, это его расшифровка. */}
        {(fixAt || coords) && (
          <div className="tnum -mt-0.5 text-right text-[11px] leading-tight text-mist">
            {fixAt && <div>{tt(fixAt.key, fixAt.vars)}</div>}
            {coords && <div>{coords}</div>}
          </div>
        )}
        <Row
          label={tt('models.vessel.sogCourse')}
          value={`${
            sog != null ? tt('models.vessel.knots', { value: formatNumber(lang, sog, 1) }) : '—'
          } · ${cog != null ? `${Math.round(cog)}°` : '—'}`}
        />
      </dl>

      {/* Только у закреплённой карточки: незакреплённая прозрачна для мыши, и
          кнопка в ней была бы недостижима. */}
      {/* Трек судна — там же, где кнопка обновления: оба действия про ЭТО судно.
          Идёт первым: путь спрашивают чаще, чем свежесть точки. */}
      {pinned && mmsi && track && (
        <div className="mt-2 border-t border-hull pt-2">
          <button
            type="button"
            onClick={track.onToggle}
            disabled={track.busy}
            className="w-full rounded-md border border-hull px-2 py-1 text-[11px] font-medium text-foam transition-colors hover:bg-haze disabled:cursor-wait disabled:text-mist"
          >
            {track.busy
              ? tt('models.vesselTrack.building')
              : track.visible
                ? tt('models.vesselTrack.hideTrack')
                : tt('models.vesselTrack.showTrack')}
          </button>
          {track.hint && (
            <div className="mt-1 text-center text-[10px] text-mist">{track.hint}</div>
          )}
        </div>
      )}

      {/* Прогнозный ход — сразу под треком: пройденный путь и предполагаемый
          читаются вместе. */}
      {pinned && mmsi && forecast && (
        <div className="mt-2 border-t border-hull pt-2">
          <button
            type="button"
            onClick={forecast.onToggle}
            className="w-full rounded-md border border-hull px-2 py-1 text-[11px] font-medium text-foam transition-colors hover:bg-haze"
          >
            {forecast.visible
              ? tt('models.vesselForecast.hide')
              : tt('models.vesselForecast.show')}
          </button>
          {/* Оговорку показываем ТОЛЬКО при включённом тумблере: до нажатия
              рассказывать, почему линии не будет, значит спорить с кнопкой,
              которую ещё не трогали. */}
          {forecast.visible && forecast.hint && (
            <div className="mt-1 text-center text-[10px] text-mist">{forecast.hint}</div>
          )}
        </div>
      )}

      {pinned && mmsi && (
        <div className="mt-2 border-t border-hull pt-2">
          <button
            type="button"
            onClick={refresh}
            disabled={busy}
            className="w-full rounded-md border border-hull px-2 py-1 text-[11px] font-medium text-foam transition-colors hover:bg-haze disabled:cursor-wait disabled:text-mist"
          >
            {busy ? tt('models.vessel.refreshing') : tt('models.vessel.refreshButton')}
          </button>
          {progress && (
            <div className="mt-1 text-center text-[10px] text-mist">
              {vesselRefreshLabel(progress)}
            </div>
          )}
        </div>
      )}
    </MapCallout>
  )
}

function Row({
  label,
  value,
  title,
  children,
}: {
  label: string
  value?: string
  /** Полное значение под курсором: карточка узкая (w-60), а строки реестра NPFC
   *  длинные («Other fishing vessels [FISHING VESSELS NOT SPECIFIED] (FX)») —
   *  без подсказки обрезанное значение не дочитать. */
  title?: string
  children?: ReactNode
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="shrink-0 text-faint">{label}</dt>
      <dd className="min-w-0 truncate text-right font-medium text-foam" title={title}>
        {children ?? value}
      </dd>
    </div>
  )
}
